import { LabelAccomodationInfo, LabelAlignmentInfo } from "./ServiceNamePositioning.types";

class ServiceNamePositioningService {

  /**
   * Retrieves the font size of the provided label
   */
  getLabelFontSize(element: Element): number {
    const { fontSize } = window.getComputedStyle(element);
    return parseFloat(fontSize);
  }

  /**
   * Determine the total area occupied by the overlapped service and layout labels using their height
   */
  determineAreaOccupiedByLabels(occupiedSpace: number, label: HTMLSpanElement): number {

    const { height: serviceLabelHeight } = label.getBoundingClientRect();
    const status: string = label.nextElementSibling.getAttribute("status");

    // Do not show the layout label for a closed section
    const layoutLabelHeight: number = status === "close" ? 0 : this.getLabelFontSize(label.nextElementSibling);

    // Add height of the overlapped service and layout labels
    occupiedSpace = occupiedSpace + serviceLabelHeight + layoutLabelHeight;
    return occupiedSpace;
  }

  /**
   * Inorder to accomodate all the labels, keep reducing the font size by 1 for each service label until all the labels fit into the section
   */
  accomodateLabelsWithinSection(props: LabelAccomodationInfo): number {

    const minimumFontSize = 20;
    let totalAreaOccupiedByLabels = 0;
    let serviceLabelFontSizeInPixel = 0;

    do {

      totalAreaOccupiedByLabels = 0;

      props.OverlapLabelList.forEach(item => {

        serviceLabelFontSizeInPixel = this.getLabelFontSize(props.Labels[item]) - 1;

        // Assign the reduced fontsize
        props.Labels[item].style.fontSize = `${serviceLabelFontSizeInPixel}px`;

        totalAreaOccupiedByLabels = this.determineAreaOccupiedByLabels(totalAreaOccupiedByLabels, props.Labels[item]);
      });

      totalAreaOccupiedByLabels = totalAreaOccupiedByLabels + this.calculateMinimumFreeSpace(props.OverlapLabelList.length);

    } while (totalAreaOccupiedByLabels > props.SectionHeight && serviceLabelFontSizeInPixel >= minimumFontSize)

    return totalAreaOccupiedByLabels;
  }

  /**
   * Assign css styles to the label, inorder to appear at the center of the parent div i.e. md-button
   */
  positionTheLabels(labelPositionFromTop: number, label: HTMLSpanElement): number {

    label.style.top = `${labelPositionFromTop}px`;

    const {width, height} = label.getBoundingClientRect();
    label.style.width = `${width}px`;
    label.style.margin = "0px auto";
    label.style.left = "0px";
    label.style.right = "0px";
    label.style.opacity = "1";

    return height;
  }

  /**
   * Sort the labels, inorder to group overlapping labels together
   */
  sortServiceLayoutLabelList(a: HTMLSpanElement, b: HTMLSpanElement): number {

    // Sort by top-position of the sections
    if (a.offsetParent instanceof HTMLElement && b.offsetParent instanceof HTMLElement) {
      if (a.offsetParent.offsetTop > b.offsetParent.offsetTop) return 1;
      if (a.offsetParent.offsetTop < b.offsetParent.offsetTop) return -1;
    }

    const aCoordinates = a.getBoundingClientRect();
    const aLeftPosition = Math.floor(aCoordinates.x);
    const aOverlapArea = Math.floor(aLeftPosition + aCoordinates.width);

    const bCoordinates = b.getBoundingClientRect();
    const bLeftPosition = Math.floor(bCoordinates.x);
    const bOverlapArea = Math.floor(bLeftPosition + bCoordinates.width);

    // Sort by x-position of the labels
    if (aLeftPosition > bLeftPosition) return 1;
    if (aLeftPosition < bLeftPosition) return -1;

    // Sort by x-position + width of the labels
    if (aOverlapArea > bOverlapArea) return -1;
    if (aOverlapArea < bOverlapArea) return 1;

    // Sort by labels in alphabetical order
    if (a.innerText > b.innerText) return 1;
    if (a.innerText < b.innerText) return -1;

    return -1;
  }

  /**
   * Calculates the sum of minimum free space required within a section.
   * This free space is needed for separating each (section and layout) label pair by atleast "5px" margin on top and bottom.
   */
  calculateMinimumFreeSpace(labelCount: number): number {

    const minimumFreeSpaceBetweenLabels = 5;

    /*
    * Add "5px" free space for each label pair to the total area. e.g. For 3 pairs, free space will be 3 * 5 = 15px.
    * Add one additional "5px", for showing free space below the last pair. i.e. 15 + 5 = 20px.
    */
    return (labelCount + 1) * minimumFreeSpaceBetweenLabels;
  }

  /**
   * Vertically align the service and layout labels.
   * Start aligning the labels from top to bottom in the following pattern:
   * 1. Top margin e.g. 5px
   * 2. Service label name e.g. ALL DAY SERVICE
   * 3. Layout label name e.g. DINING ROOM
   * 4. Bottom margin e.g. 5px
   */
  alignOverlappingLabels(props: LabelAlignmentInfo): void {

    // Contains the top position value in pixel
    let labelPositionFromTop = 0;

    props.OverlapLabelList.forEach(item => {

      // Align service label
      labelPositionFromTop = labelPositionFromTop + props.FreeSpaceBetweenEachLabel;
      const labelHeight: number = this.positionTheLabels(labelPositionFromTop, props.Labels[item]);
      labelPositionFromTop = labelPositionFromTop + labelHeight;

      /*
      * Align layout label
      * Check sibling is a <span> element, inorder to get access to its style properties.
      */
      if (props.Labels[item].nextElementSibling instanceof HTMLSpanElement) {

        const layoutLabel = props.Labels[item].nextElementSibling as HTMLSpanElement;

        const isSectionClosed = layoutLabel.getAttribute("status") === "close";
        if (!isSectionClosed) {

          layoutLabel.style.display = "inline-block";
          this.positionTheLabels(labelPositionFromTop, layoutLabel);
          labelPositionFromTop = labelPositionFromTop + this.getLabelFontSize(props.Labels[item].nextElementSibling);
        }
        else {
          layoutLabel.style.position = "static";
          layoutLabel.style.opacity = "1";
        }
      }
    });
  }
}

// creates a singleton instance to export
const instance = new ServiceNamePositioningService();
export {instance as PositioningServiceInstance};